import { createSlice } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { getAccessToken } from "@/utils/token_functions";
import { BASE_URL } from "@/config/api";
import { secureAxios } from "@/middleware/encryptionMiddleware";

// Create sub-event thunk
export const createSubEvent = createAsyncThunk(
  "subEvent/create",
  async (subEventData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.post(
        `${BASE_URL}/v1/subEvent`,
        subEventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Update this thunk to handle 404 errors gracefully
export const fetchSubEventsByOrg = createAsyncThunk(
  "subEvent/fetchByOrg",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.get(
        `${BASE_URL}/v1/subEvent/by-any/${key}/${value}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.data;
    } catch (error) {
      // If it's a 404 error (not found), return an empty array instead of rejecting
      if (error.response?.status === 404) {
        return []; // Return empty array for "not found" instead of rejecting
      }
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const updateSubEvent = createAsyncThunk(
  "subEvent/update",
  async (subEventData, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/subEvent`,
        subEventData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

export const deleteSubEvent = createAsyncThunk(
  "subEvent/delete",
  async (subEventId, { rejectWithValue }) => {
    try {
      const token = getAccessToken();

      // Use secureAxios instead of regular axios
      const response = await secureAxios.delete(`${BASE_URL}/v1/subEvent`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: [subEventId], // Send as array similar to event delete
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Add a schedule to a sub-event
export const addScheduleToSubEvent = createAsyncThunk(
  "subEvent/addSchedule",
  async ({ subEventId, scheduleData }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.post(
        `${BASE_URL}/v1/subEvent/${subEventId}/schedule`,
        scheduleData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Remove a schedule from a sub-event
export const removeScheduleFromSubEvent = createAsyncThunk(
  "subEvent/removeSchedule",
  async ({ subEventId, scheduleId }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.delete(
        `${BASE_URL}/v1/subEvent/${subEventId}/schedule/${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return { ...response.data, subEventId, scheduleId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

// Update a specific schedule in a sub-event
export const updateScheduleInSubEvent = createAsyncThunk(
  "subEvent/updateSchedule",
  async ({ subEventId, scheduleId, scheduleData }, { rejectWithValue }) => {
    try {
      const token = getAccessToken();
      const response = await secureAxios.patch(
        `${BASE_URL}/v1/subEvent/${subEventId}/schedule/${scheduleId}`,
        scheduleData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return { ...response.data, subEventId, scheduleId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    meta: {
      requiresNetwork: true,
    },
  }
);

const initialState = {
  step1: {
    name: "",
    description: "",
    eventType: "", // physical, virtual, or physical_and_virtual
    duration: {
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      period: "", // 24, 48, or custom
    },
    location: {
      type: "",
      address: "",
      virtualService: "", // third_party or vvc
      url: "",
    },
    members: [], // Array of member objects with roles
    manifestoID: "",
    vvcSchedule: null,
  },
  step2: {
    schedule: {
      name: "",
      speakers: [],
      duration: {
        startTime: "",
        endTime: "",
      },
    },
    images: [],
  },
  step3: {
    logistics: {
      details: "",
      type: "",
    },
    officerInCharge: [], // Array of officer images/info
    budgetaryAllocation: 0,
  },
  currentStep: 1,
  eventID: "",
  organizationID: "",
  isLoading: false,
  error: null,
  success: false,
  subEvents: [],
};

const subEventSlice = createSlice({
  name: "subEvent",
  initialState,
  reducers: {
    updateStep: (state, action) => {
      const { step, data } = action.payload;
      state[step] = { ...state[step], ...data };
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    resetSubEvent: () => initialState,

    loadCachedData: (state, action) => {
      return {
        ...state,
        subEvents: action.payload.subEvents || state.subEvents,
        step1: action.payload.step1 || state.step1,
        step2: action.payload.step2 || state.step2,
        step3: action.payload.step3 || state.step3,
        // Keep form state
        currentStep: state.currentStep,
        isLoading: false,
        error: null,
        // Flag to indicate data is from offline cache
        isOfflineData: true,
      };
    },
    resetSubEvents: (state) => {
      state.subEvents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSubEvent.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(createSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSubEventsByOrg.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSubEventsByOrg.fulfilled, (state, action) => {
        state.isLoading = false;
        state.subEvents = action.payload;
        state.error = null;
      })
      .addCase(fetchSubEventsByOrg.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSubEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        if (action.payload && action.payload.data) {
          const updatedSubEvent = action.payload.data;

          // Find and update the specific subEvent in our array
          const index = state.subEvents.findIndex(
            (event) => event._id === updatedSubEvent._id
          );
          if (index !== -1) {
            state.subEvents[index] = updatedSubEvent;
          }
        }
      })
      .addCase(updateSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSubEvent.fulfilled, (state) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(deleteSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Handle addScheduleToSubEvent actions
      .addCase(addScheduleToSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addScheduleToSubEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        // Update the subEvent in the store with the new schedule
        if (action.payload && action.payload.data) {
          const updatedSubEvent = action.payload.data;

          // Find and update the specific subEvent in our array
          const index = state.subEvents.findIndex(
            (event) => event._id === updatedSubEvent._id
          );
          if (index !== -1) {
            state.subEvents[index] = updatedSubEvent;
          }
        }
      })
      .addCase(addScheduleToSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Handle removeScheduleFromSubEvent actions
      .addCase(removeScheduleFromSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeScheduleFromSubEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        // Update the subEvent in the store by removing the deleted schedule
        if (action.payload && action.payload.subEventId) {
          const subEventId = action.payload.subEventId;
          const scheduleId = action.payload.scheduleId;

          // Find the subEvent in our array
          const index = state.subEvents.findIndex(
            (event) => event._id === subEventId
          );
          if (index !== -1) {
            // Update the schedule array to filter out the removed schedule
            if (
              state.subEvents[index].schedule &&
              Array.isArray(state.subEvents[index].schedule)
            ) {
              state.subEvents[index].schedule = state.subEvents[
                index
              ].schedule.filter((schedule) => schedule._id !== scheduleId);
            }
          }
        }
      })
      .addCase(removeScheduleFromSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Handle updateScheduleInSubEvent actions
      .addCase(updateScheduleInSubEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateScheduleInSubEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.error = null;

        // Update the subEvent in the store with the updated schedule
        if (action.payload && action.payload.data) {
          const updatedSubEvent = action.payload.data;

          // Find and update the specific subEvent in our array
          const index = state.subEvents.findIndex(
            (event) => event._id === updatedSubEvent._id
          );
          if (index !== -1) {
            state.subEvents[index] = updatedSubEvent;
          }
        }
      })
      .addCase(updateScheduleInSubEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updateStep,
  setCurrentStep,
  resetSubEvent,
  loadCachedData,
  resetSubEvents,
} = subEventSlice.actions;

// Selectors
export const selectSubEventStep1 = (state) => state.subEvent.step1;
export const selectSubEventStep2 = (state) => state.subEvent.step2;
export const selectSubEventStep3 = (state) => state.subEvent.step3;
export const selectCurrentStep = (state) => state.subEvent.currentStep;
export const selectSubEventLoading = (state) => state.subEvent.isLoading;
export const selectSubEventError = (state) => state.subEvent.error;
export const selectSubEventSuccess = (state) => state.subEvent.success;
export const selectSubEvents = (state) => state.subEvent.subEvents;
export const selectIsOfflineData = (state) =>
  state.subEvent.isOfflineData || false;

export default subEventSlice.reducer;
